import { html, css } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { TeComponent } from './common/te-component';
import { pathByName } from './icons/icon.utils';
import { styleMap } from 'lit/directives/style-map';
import { TeDropdownOption } from './te-dropdown-option';
import './te-text';
import './te-dropdown-option';
import './te-icon';
import { theme } from './common/theme';
import './te-loader';

export type DropdownSkin = 'gray' | 'default';
export type IconName = keyof typeof pathByName;

/**
 * Dropdown Component
 *
 * @slot - Default slot for dropdown options
 */
@customElement('te-dropdown')
export class TeDropdown extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-dropdown-container {
        display: block;
      }

      .dropdown-button {
        height: 40px;
        display: flex;
        align-items: center;
        z-index: 2;
        width: 100%;
        background: ${theme.colors.shade0};
        border: 1px solid ${theme.colors.shade2};
        border-radius: 4px;
        padding: 8px 0;
        position: relative;
      }

      #placeholder {
        font-style: italic;
      }

      .dropdown-button.loading {
        pointer-events: none;
      }

      .dropdown-button.disabled {
        pointer-events: none;
        background: ${theme.colors.shade2};
      }

      .te-dropdown-option-container {
        flex-direction: column;
        position: absolute;
        background: ${theme.colors.shade0};
        width: 256px;
        max-height: 200px;
        z-index: 1;
        border: 1px solid ${theme.colors.shade2};
        border-radius: 4px;
        box-shadow: ${theme.shadows.secondary};
        overflow: hidden;
      }

      .current-label-text {
        font-style: italic;
      }

      .dropdown-label-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
      }

      .display-text {
        color: ${theme.colors.text};
        padding-right: 16px;
      }

      .display-text te-dropdown-option img {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        margin-right: 8px;
        border: none;
        pointer-events: none;
      }

      .placeholder-text {
        padding-left: 16px;
        opacity: 0.94;
      }

      .dropdown-label-container te-icon,
      .dropdown-label-container te-loader {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    `,
  ];

  /** Icon name from icon pack */
  @property() icon: IconName = 'triangle-down';
  /** Dropdown is in a loading state */
  @property({ type: Boolean }) loading = false;
  /** Exact width of dropdown */
  @property({ type: Number }) width = 256;
  /** Dropdown is disabled */
  @property({ type: Boolean }) disabled = false;
  /** Dropdown has option to clear it's value */
  @property({ type: Boolean }) clearable = false;
  /** Text value for clear option */
  @property({ type: String }) clearOptionText = '–';
  /** Basic color scheme of dropdown */
  @property({ type: String }) skin: DropdownSkin = 'default';
  /** Text before any value is received */
  @property({ type: String }) placeholderText = '';

  @state() isOpen = false;
  @state() selected?: TeDropdownOption;
  @state() selectedValue?: number;
  @state() textToDisplay?: unknown;
  @state() onDocumentListenFn: (e: Event) => void;

  @query('#placeholder') _placeholderSlot: HTMLSlotElement | undefined;

  isTeDropdownOption(elem: Element): elem is TeDropdownOption {
    return elem.nodeName === 'TE-DROPDOWN-OPTION';
  }

  constructor() {
    super();
    this.onDocumentListenFn = this.onDocumentListen.bind(this);
  }

  onDocumentListen(e: Event) {
    const dropdownOptionElem = (e.target as Element)?.shadowRoot?.querySelector(
      'te-dropdown-option'
    );
    if (dropdownOptionElem?.nodeName === 'TE-DROPDOWN-OPTION') return;
    this.isOpen = false;
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentListenFn);
  }

  firstUpdated() {
    for (const child of Array.from(this.children) as TeDropdownOption[]) {
      if (child.slot) continue;

      child.onclick = (ev) => {
        if (child.disabled || !this.handleChange) return;

        this.handleChange(ev);
        this.isOpen = false;
      };
    }
  }

  updated() {
    if (!this.isOpen) {
      document.removeEventListener('click', this.onDocumentListenFn);
    } else {
      document.addEventListener('click', this.onDocumentListenFn);
    }
    const textToDisplay = this.shadowRoot?.querySelector('.display-text');

    // To append selected option
    const children = Array.from(this.children) as TeDropdownOption[];
    this.selected = children.find((child) => child.selected);
    if (textToDisplay && this.selected) {
      const ele = new TeDropdownOption(this.selected);
      while (textToDisplay.firstChild) textToDisplay.firstChild.remove();
      textToDisplay.appendChild(ele);
    }
  }

  getDropdownWidth() {
    return { width: `${this.width}px` };
  }

  getDropdownVisibility() {
    return {
      background:
        this.skin === 'gray'
          ? this.isOpen
            ? `${theme.colors.shade2}`
            : 'transparent'
          : `${theme.colors.shade0}`,
      marginTop: this.isOpen ? '4px' : 'unset',
      pointerEvents: this.loading || this.disabled ? 'none' : 'auto',
    };
  }

  openDropdownHandler() {
    if (this.loading || this.disabled) return;
    this.isOpen = !this.isOpen;
  }

  getDropdownButtonStyle() {
    const background = this.skin === 'gray' ? `${theme.colors.shade2}` : `${theme.colors.shade0}`;
    return {
      background,
      cursor: this.loading || this.disabled ? 'default' : 'pointer',
    };
  }

  renderComponent() {
    if (this.loading) {
      return html`
        <te-loader size="16"></te-loader>
      `;
    }

    return html`
      <te-icon
        name=${this.icon}
        color="${this.disabled ? theme.colors.shade3.toString() : theme.colors.shade5.toString()}"
        size="12"
      ></te-icon>
    `;
  }

  onClearSelection(e: Event) {
    this.isOpen = false;
    this.selected = undefined;
    this.handleDropdownClear(e);
  }

  render() {
    const classes = [
      'dropdown-button',
      this.loading ? 'loading' : '',
      this.disabled ? 'disabled' : '',
    ].filter(Boolean);
    const placeholderClasses = ['placeholder-text', this.disabled ? 'disabled' : ''].filter(
      Boolean
    );
    return html`
      <div
        class="te-dropdown-container"
        id="te-dropdown-container"
        style=${styleMap(this.getDropdownWidth())}
      >
        <div
          class="${classes.join(' ')}"
          @click="${this.openDropdownHandler}"
          id=${this.testId}
          style=${styleMap(this.getDropdownButtonStyle())}
        >
          <span class="dropdown-label-container">
            ${this.selected
              ? html`
                  <div class="display-text"></div>
                `
              : html`
                  <te-text
                    italic
                    class=${placeholderClasses.join(' ')}
                    color=${this.disabled
                      ? `${theme.colors.shade3.toString()}`
                      : ` ${theme.colors.shade4.toString()}`}
                  >
                    ${this.placeholderText}
                  </te-text>
                `}
            ${this.renderComponent()}
          </span>
        </div>
        ${this.isOpen
          ? html`
              <div
                class="te-dropdown-option-container"
                id="te-dropdown-option-container"
                style=${styleMap(this.getDropdownVisibility())}
              >
                ${this.clearable
                  ? html`
                      <te-dropdown-option
                        @click=${this.onClearSelection}
                        text=${this.clearOptionText}
                      ></te-dropdown-option>
                    `
                  : null}
                <slot id="dropdown-list"></slot>
              </div>
            `
          : null}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-dropdown': TeDropdown;
  }
}
